/**
 * How one assignment is marked, in three tabs: Brief · Evaluation · Setup.
 *
 * > Owner, 2026-10-05: *"on each assigma lets have tabs inisde Assigments brief, evaluation and
 * > by last setups1 and well orgnaiaze and much less verbose! and the tests with a better UX UI!
 * > … also the How craft reads each language is not needed!"*
 *
 * 🔴 ONE PANEL, ONE SAVE. It replaced the Binder: a column of controls with two Save buttons — a
 * Preview/Apply pair at its foot and the tests' own Save in its middle — and a paragraph under
 * nearly every control. Here every change, on any tab, is one unsaved change in the save bar, and
 * the tab it was made on carries a dot.
 * 🔴 A CHANGE THAT RE-GRADES IS SAID BEFORE IT IS SAVED. The rubric, the engine, what a criterion
 * does, the assignment's own criteria and whether students' own tests are expected decide what a
 * mark is made of, and the server archives and re-queues every grade when one changes. So Save
 * asks the server first: when it would archive anything, the bar shows the server's own sentence
 * and waits for a second click; when it would archive nothing, it saves. The client's guess only
 * labels the button ("Review and save"); the server's answer decides.
 * ⚠️ THE TESTS SAVE WITH IT AND RE-GRADE NOTHING: the next runs use them.
 * ⚠️ "HOW CRAFT READS IN EACH LANGUAGE" IS GONE FROM HERE (owner, same day). It was read-only, it
 * is the same for every assignment, and the Grading page's Languages tab is where it is edited.
 */
import { type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  type ApiError, type Assignment, type Binding, type Criterion, type CriterionOverride,
  type ExtraCriterion, type HarnessOffer, putBinding, type RubricInfo, type TaskDraft,
} from "../api";
import { type CriterionMode, effectivePoints, type Refusal } from "../rubric";
import { tidyTask, withIds } from "../tasks";
import { AssignmentTasks, type TaskHost } from "./AssignmentTasks";
import { tracked } from "../useAsync";
import { Badge, ErrorBox, Markdown, Why } from "../ui";
import { criterionOf, type CriterionChoice, TestList, TestsFoot, type TestsDraft,
         useTeacherTests } from "./TeacherTests";

type Tab = "brief" | "evaluation" | "setup";
const TABS: { id: Tab; label: string }[] = [
  { id: "brief", label: "Brief" }, { id: "evaluation", label: "Evaluation" },
  { id: "setup", label: "Setup" }];

/** One unsaved change, as the save bar names it. */
interface Change { what: string; regrades: boolean; tab: Tab }

const EMPTY: Binding = {
  rubric_id: null, engine_id: null, auto_grade: null, guidance: "", rubric_set_by: null,
  engine_set_by: null, testing_required: null, extra_criteria: [], criteria_overrides: {},
};

// ── what a criterion does here ──────────────────────────────────────────────────

const BLANK: CriterionOverride = { mode: "score", credit_for: [], replaces: false, note: "" };
const ovOf = (m: Record<string, CriterionOverride>, id: string): CriterionOverride =>
  ({ ...BLANK, ...(m[id] ?? {}) });
/** An override as the server will store it. ⚠️ AN OFF CRITERION SENDS NO WORDING — the server
 *  refuses lines that would reach nothing — but the draft keeps them, so switching it back on
 *  gives back what was typed. */
function tidy(o: CriterionOverride): CriterionOverride {
  if (o.mode === "off") return { ...BLANK, mode: "off" };
  const credit_for = o.credit_for.map((x) => x.trim()).filter(Boolean);
  return { mode: o.mode, credit_for, replaces: o.replaces && credit_for.length > 0,
           note: o.note.trim() };
}
const bare = (o: CriterionOverride) =>
  o.mode === "score" && o.credit_for.length === 0 && !o.replaces && o.note === "";
/** Every override worth keeping, tidied. ⚠️ A CRITERION BACK AT ITS DEFAULT LEAVES NO ROW: an
 *  entry saying "score, nothing added" is no entry in effect, and keeping it would report a
 *  change where a teacher undid one. */
function tidyAll(m: Record<string, CriterionOverride>): Record<string, CriterionOverride> {
  const out: Record<string, CriterionOverride> = {};
  for (const k of Object.keys(m).sort()) {
    const t = tidy(ovOf(m, k));
    if (!bare(t)) out[k] = t;
  }
  return out;
}
const sameLines = (a: string[], b: string[]) => a.join("\n") === b.join("\n");

const MODES: { id: CriterionMode; label: string }[] = [
  { id: "score", label: "Counts" }, { id: "advisory", label: "Advisory" },
  { id: "off", label: "Off" }];
const MODE_WORD: Record<CriterionMode, string> = { score: "counts", advisory: "advisory", off: "off" };

/** An extra criterion as the server stores it. Ids are positional (`X1…Xn`) and the server's to
 *  assign, so they are left out. */
const tidyExtra = (c: ExtraCriterion) => ({
  label: (c.label ?? "").trim(), weight: Number(c.weight) || 0,
  credit_for: (c.credit_for ?? []).map((x) => x.trim()).filter(Boolean) });
const sameExtra = (a: ExtraCriterion, b: ExtraCriterion) =>
  JSON.stringify(tidyExtra(a)) === JSON.stringify(tidyExtra(b));

const REFUSAL: Record<Refusal, (n: { total: number; max: number; zeroed: string[] }) => string> = {
  all_off: () => "Every criterion is off, so there is nothing to mark against.",
  none_scored: (n) => `Nothing counts, so the highest mark here would be 0 of ${n.max}.`,
  no_base_scored: () => "At least one of the rubric's own criteria has to count. Make the others advisory instead.",
  over: (n) => `Your criteria are worth ${n.total} of ${n.max}, which leaves the rubric's own nothing. Keep them under ${n.max}.`,
  zeroed: (n) => `At ${n.total} points your criteria scale ${n.zeroed.join(", ")} down to 0. Use fewer or lighter ones.`,
};

export function AssignmentSetup({ courseId, cw, rubrics, defaultRubricId, harnesses,
                                  sysEngine, sysAuto, onChanged, canEdit = true }: {
  courseId: string; cw: Assignment; rubrics: RubricInfo[]; defaultRubricId: string;
  harnesses: HarnessOffer[]; sysEngine: string; sysAuto: boolean;
  onChanged: () => void;
  /** `can_edit_rubrics`. Without it the panel still OPENS — what an assignment is marked against
   *  is for every teacher to read — and every control that changes something is disabled. */
  canEdit?: boolean;
}) {
  const [tab, setTab] = useState<Tab>("brief");
  // 🔴 WHAT THIS PANEL LAST WROTE, until the listing it came from is read again. The binding is
  // the parent's, from a Classroom listing that takes seconds to come back; without this the
  // saved values would flash back to the old ones while it does.
  const [written, setWritten] = useState<{ from: Assignment["binding"]; to: Binding } | null>(null);
  const b: Binding = (written && written.from === cw.binding ? written.to : cw.binding) ?? EMPTY;

  // "" = leave alone · "inherit" = clear back to the default · a value = set it.
  const [engine, setEngine] = useState("");
  const [auto, setAuto] = useState("");
  const [tests, setTests] = useState("");
  const [rubricSel, setRubricSel] = useState("");
  // null = untouched.
  const [overrides, setOverrides] = useState<Record<string, CriterionOverride> | null>(null);
  const [guidance, setGuidance] = useState<string | null>(null);
  const [extras, setExtras] = useState<ExtraCriterion[] | null>(null);
  const [taskDraft, setTaskDraft] = useState<TaskDraft[] | null>(null);
  const [summaryDraft, setSummaryDraft] = useState<string | null>(null);
  // Bumped by Discard, so the criteria's own text boxes start again from the saved lines.
  const [round, setRound] = useState(0);
  const t = useTeacherTests(courseId, cw.course_work_id);

  const [review, setReview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [said, setSaid] = useState("");

  // ⚠️ SAME SENTINELS AS EVERY OTHER FIELD, resolved the same way, or the points below would be
  // computed against the rubric that is being replaced.
  const rubricId = rubricSel === "inherit" ? defaultRubricId
    : (rubricSel || b.rubric_id || defaultRubricId);
  const rubric = rubrics.find((x) => x.id === rubricId);
  const selectable = rubrics.filter((x) => x.selectable);
  const savedOv = b.criteria_overrides ?? {};
  const curOv = overrides ?? savedOv;
  const savedEx = b.extra_criteria ?? [];
  const curEx = extras ?? savedEx;
  const curGuidance = guidance ?? b.guidance ?? "";
  const savedTasks = b.tasks ?? [];
  const curTasks = taskDraft ?? savedTasks;
  // ⚠️ THE IDS THE SERVER WILL GIVE THEM, subtasks included, so a change is named by the task it
  // is in and a removed one by the id it was saved under.
  const tasksNow = withIds(curTasks, savedTasks).map((x) => ({ ...tidyTask(x), id: x.id }));
  const tasksChanged = taskDraft !== null
    && JSON.stringify(tasksNow) !== JSON.stringify(savedTasks.map(tidyTask));
  const savedSummary = b.tasks_summary ?? "";
  const curSummary = summaryDraft ?? savedSummary;
  const summaryChanged = summaryDraft !== null && summaryDraft.trim() !== savedSummary.trim();

  const ovNow = useMemo(() => tidyAll(curOv), [curOv]);
  const ovWas = useMemo(() => tidyAll(savedOv), [savedOv]);
  const ovChanged = JSON.stringify(ovNow) !== JSON.stringify(ovWas);
  const exChanged = extras !== null && (extras.length !== savedEx.length
    || extras.some((c, i) => {
      const was = savedEx[i];
      return was === undefined || sameExtra(c, was) === false || (c.id ?? "") !== (was.id ?? "");
    }));
  const guidanceChanged = guidance !== null && guidance !== (b.guidance ?? "");

  const changes = useMemo((): Change[] => {
    const out: Change[] = [];
    if (guidanceChanged) out.push({ what: "notes for the grader", regrades: false, tab: "brief" });
    // ⚠️ THE SUMMARY IS CONTEXT, LIKE THE NOTES: changing it re-grades nothing.
    if (summaryChanged) out.push({ what: "what the assignment is", regrades: false, tab: "brief" });
    if (tasksChanged) {
      // ⚠️ BY THE ID IT WAS SAVED UNDER, as the extras are, so a removed T2 reads as removed.
      const was = new Map(savedTasks.map((x) => [x.id ?? "", tidyTask(x)]));
      const kept = new Set(tasksNow.map((x) => x.id));
      for (const x of tasksNow) {
        const w = was.get(x.id);
        if (w && JSON.stringify(w) !== JSON.stringify(x)) out.push({ what: `${x.id} changed`, regrades: true, tab: "brief" });
      }
      for (const x of savedTasks) {
        if (!kept.has(x.id ?? "")) out.push({ what: `${x.id} removed`, regrades: true, tab: "brief" });
      }
      const added = tasksNow.filter((x) => !was.has(x.id)).length;
      if (added) out.push({ what: `${added} task${added === 1 ? "" : "s"} added`, regrades: true, tab: "brief" });
    }
    // Per criterion: its mode, its lines, its note — only the first two re-grade.
    for (const id of [...new Set([...Object.keys(ovNow), ...Object.keys(ovWas)])].sort()) {
      const now = ovNow[id] ?? BLANK;
      const was = ovWas[id] ?? BLANK;
      if (now.mode !== was.mode) out.push({ what: `${id} ${MODE_WORD[now.mode]}`, regrades: true, tab: "evaluation" });
      if (!sameLines(now.credit_for, was.credit_for) || now.replaces !== was.replaces) {
        out.push({ what: `${id}'s lines`, regrades: true, tab: "evaluation" });
      }
      if (now.note !== was.note) out.push({ what: `${id}'s note`, regrades: false, tab: "evaluation" });
    }
    if (extras !== null) {
      // ⚠️ BY THE ID IT WAS SAVED UNDER, so removing X1 reads as X1 removed — not as X1 changed
      // into X2 and one fewer at the end.
      const was = new Map(savedEx.map((c) => [c.id ?? "", c]));
      const kept = new Set(extras.map((c) => c.id ?? ""));
      const added = extras.filter((c) => !c.id || !was.has(c.id)).length;
      for (const c of extras) {
        const w = c.id ? was.get(c.id) : undefined;
        if (w && !sameExtra(c, w)) out.push({ what: `${c.id} changed`, regrades: true, tab: "evaluation" });
      }
      for (const c of savedEx) {
        if (!kept.has(c.id ?? "")) out.push({ what: `${c.id} removed`, regrades: true, tab: "evaluation" });
      }
      if (added) out.push({ what: `${added} criteri${added === 1 ? "on" : "a"} added`, regrades: true, tab: "evaluation" });
    }
    if (tests) out.push({ what: "students' own tests", regrades: true, tab: "evaluation" });
    for (const c of t.changes) out.push({ what: c, regrades: false, tab: "evaluation" });
    if (rubricSel) out.push({ what: "rubric", regrades: true, tab: "setup" });
    if (engine) out.push({ what: "engine", regrades: true, tab: "setup" });
    if (auto) out.push({ what: "unattended grading", regrades: false, tab: "setup" });
    return out;
  }, [guidanceChanged, summaryChanged, tasksChanged, tasksNow, savedTasks, ovNow, ovWas, extras,
      savedEx, tests,
      t.changes, rubricSel, engine, auto]);
  useEffect(() => { if (changes.length) setSaid(""); }, [changes.length]);
  const binderDirty = guidanceChanged || tasksChanged || summaryChanged || ovChanged || exChanged || tests !== ""
    || rubricSel !== "" || engine !== "" || auto !== "";

  // 🔴 THE NUDGE READS THE PENDING STATE, so it goes the moment a teacher writes the first line —
  // the same rule as `rubric.is_specific` on the server, which the Overview's `bound` reads.
  const specific = curEx.length > 0 || curGuidance.trim() !== "" || curTasks.length > 0
    || Object.values(ovNow).some((o) => o.credit_for.length > 0 || o.note !== "");

  const body = () => ({
    rubric_id: rubricSel, engine_id: engine, auto_grade: auto, testing_required: tests,
    guidance: guidanceChanged ? guidance : null,
    extra_criteria: exChanged ? curEx.map(tidyExtra) : null,
    tasks: tasksChanged ? curTasks.map(tidyTask) : null,
    tasks_summary: summaryChanged ? curSummary.trim() : null,
    criteria_overrides: ovChanged ? ovNow : null,
    confirm: false,
  });

  const clear = () => {
    setEngine(""); setAuto(""); setTests(""); setRubricSel("");
    setOverrides(null); setGuidance(null); setExtras(null); setTaskDraft(null);
    setSummaryDraft(null);
    setRound((n) => n + 1);
  };
  const discard = () => { clear(); t.discard(); setReview(null); setErr(null); setSaid(""); };

  /** What the binding is once this save lands — shown until the listing is read again. */
  const projected = (): Binding => {
    const set = <T,>(v: string, was: T, on: (x: string) => T): T =>
      (v === "" ? was : v === "inherit" ? (null as T) : on(v));
    return {
      ...b,
      rubric_id: set(rubricSel, b.rubric_id, (x) => x),
      engine_id: set(engine, b.engine_id, (x) => x),
      auto_grade: set(auto, b.auto_grade, (x) => x === "on"),
      testing_required: set(tests, b.testing_required, (x) => x === "yes"),
      guidance: curGuidance,
      extra_criteria: curEx.map((c, i) => ({ id: `X${i + 1}`, ...tidyExtra(c) })),
      criteria_overrides: ovNow,
      tasks: tasksNow,
      tasks_summary: curSummary.trim(),
    };
  };
  const done = async (wrote: boolean) => {
    setReview(null);
    if (wrote) {
      setWritten({ from: cw.binding, to: projected() });
      clear();
      // ⚠️ BEFORE THE TESTS: the binding is written whatever happens to them next, and a refused
      // test must not leave the listing showing the old one.
      onChanged();
    }
    const testsSaved = t.changes.length > 0 ? await t.save() : false;
    setSaid(wrote || testsSaved ? "Saved." : "Nothing had changed.");
  };
  const run = (fn: () => Promise<void>) => {
    setBusy(true); setErr(null); setSaid("");
    fn().catch((e) => setErr(e as ApiError)).finally(() => setBusy(false));
  };
  const save = () => run(async () => {
    if (!binderDirty) return done(false);
    const p = await tracked(putBinding(courseId, cw.course_work_id, body()));
    const would = Array.isArray(p.would_archive) ? p.would_archive.length : 0;
    // 🔴 THE SERVER DECIDES WHETHER THIS WAITS FOR A YES, not the label on the button.
    if (would > 0) { setReview(String(p.note ?? "")); return; }
    await tracked(putBinding(courseId, cw.course_work_id, { ...body(), confirm: true }));
    await done(true);
  });
  const confirm = () => run(async () => {
    await tracked(putBinding(courseId, cw.course_work_id, { ...body(), confirm: true }));
    await done(true);
  });

  // 🔴 WHAT C1 IS WORTH WITH THE CRITERIA AS THEY ARE ON SCREEN — the number the tasks split, by
  // the same mirrored arithmetic the Evaluation tab shows.
  const host = useMemo((): TaskHost => {
    const base = rubric?.criteria ?? [];
    const max = base.reduce((n, c) => n + (c.weight || 0), 0);
    const xs = curEx.map((c, i) => ({ id: `X${i + 1}`, weight: Number(c.weight) || 0 }));
    const row = effectivePoints(base, xs, (id) => ovOf(curOv, id).mode, max).rows
      .find((r) => r.id === "C1");
    const c1 = base.find((c) => c.id === "C1");
    return { label: c1?.label ?? "C1", mode: row?.mode ?? "off",
             points: row?.points ?? row?.would ?? 0 };
  }, [rubric, curEx, curOv]);

  const ids = useId();
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({ brief: null, evaluation: null, setup: null });
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = TABS.findIndex((x) => x.id === tab);
    const to = e.key === "ArrowRight" ? TABS[(i + 1) % TABS.length]
      : e.key === "ArrowLeft" ? TABS[(i + TABS.length - 1) % TABS.length]
      : e.key === "Home" ? TABS[0] : e.key === "End" ? TABS[TABS.length - 1] : undefined;
    if (!to) return;
    e.preventDefault();
    setTab(to.id);
    tabRefs.current[to.id]?.focus();
  };
  const dirtyTabs = new Set(changes.map((c) => c.tab));
  const regrades = changes.some((c) => c.regrades);

  // 🔴 EVERY CONTROL THAT CHANGES SOMETHING IS DISABLED FOR A READER, BY NAME — never by a
  // `<fieldset disabled>`, which would also disable what reading needs: every Why button, each
  // criterion's toggle and the tests' Show. A test walks the panel for a reader and fails on any
  // control left enabled outside those.
  const ro = !canEdit;
  const effAuto = auto === "inherit" ? sysAuto : auto !== "" ? auto === "on" : (b.auto_grade ?? sysAuto);

  return (
    <div className="as">
      <div className="tabs as-tabs" role="tablist" aria-label={`How ${cw.title} is marked`}>
        {TABS.map((x) => (
          <button key={x.id} type="button" role="tab" className="tab" id={`${ids}-${x.id}`}
                  aria-selected={tab === x.id} aria-controls={`${ids}-${x.id}-panel`}
                  tabIndex={tab === x.id ? 0 : -1} onKeyDown={onTabKey}
                  ref={(el) => { tabRefs.current[x.id] = el; }}
                  onClick={() => setTab(x.id)}>
            {x.label}
            {dirtyTabs.has(x.id) && <span className="unsaved"><span className="sr"> (unsaved changes)</span></span>}
          </button>))}
      </div>
      {!canEdit && (
        <p className="small muted">Read-only for you: changing how this assignment is marked needs
          the Edit rubrics permission on this cohort.</p>)}

      {/* ⚠️ EVERY TAB STAYS MOUNTED, the inactive ones hidden, so what is typed on one survives a
          look at another. */}
      <div role="tabpanel" id={`${ids}-brief-panel`} aria-labelledby={`${ids}-brief`}
           hidden={tab !== "brief"}>
        <div className="as-brief">
          <div className="as-src">
            <div className="as-src-head">
              <Badge tone="plain">from Classroom</Badge><span>read-only here</span>
              <span className="spacer" />
              {cw.alternate_link && (
                <a href={cw.alternate_link} target="_blank" rel="noreferrer">Edit in Classroom ↗</a>)}
            </div>
            {cw.description.trim()
              ? <div className="md"><Markdown text={cw.description} /></div>
              : <p className="small muted">Classroom has no description for this assignment.</p>}
          </div>
          <div className="as-notes">
            <AssignmentTasks courseId={courseId} cw={cw.course_work_id} tasks={curTasks}
                             saved={savedTasks} summary={curSummary} onSummary={setSummaryDraft}
                             host={host} hasBrief={cw.description.trim() !== ""}
                             readOnly={ro} onChange={setTaskDraft} />
            {!specific && (
              <div className="as-nudge" role="status">
                <span aria-hidden="true">💡</span>
                <span>Marked against the base criteria only. Say what this assignment requires —
                  here, or as lines on a criterion under Evaluation.</span>
              </div>)}
            <div className="as-sec-head">
              <label className="as-sec-t" htmlFor={`${ids}-notes`}>Notes for the grader</label>
              <Why label="What the grader does with these notes">
                The grader reads them with the brief, after its own fixed rules — so nothing written
                here can switch off a safety rule, the citation requirement or the abstention rule.
              </Why>
            </div>
            <textarea id={`${ids}-notes`} className="ta prose as-notes-ta" rows={8}
                      value={curGuidance} disabled={ro}
                      placeholder="What the students were asked to build, what matters most, and anything to weigh heavily."
                      onChange={(e) => setGuidance(e.target.value)} />
            <div className="as-hint">Markdown works. Notes re-grade nothing — the next runs read them.</div>
          </div>
        </div>
      </div>

      <div role="tabpanel" id={`${ids}-evaluation-panel`} aria-labelledby={`${ids}-evaluation`}
           hidden={tab !== "evaluation"}>
        {/* 🔴 THE TESTS ARE INSIDE THE CRITERIA (owner, 2026-10-05): each proves a task works and
            counts under the criterion it sits in. They gate their own edits on the server's
            `may_edit`; Show stays for everybody. */}
        <Criteria key={round} base={rubric?.criteria ?? []} extras={curEx} overrides={curOv}
                  onExtras={setExtras} onOverrides={setOverrides} readOnly={ro} tests={t}
                  tasks={tasksNow.flatMap((x) => [x, ...(x.subtasks ?? [])])
                    .filter((x) => x.title).map((x) => `${x.id} · ${x.title}`)} />
        <section className="as-sec" aria-label="Students' own tests">
          {/* ⚠️ "STUDENTS' OWN TESTS", NOT "TESTS EXPECTED": beside the teacher's tests just above,
              that read as a question about those. */}
          <div className="set-rows">
            <Inherited
              label="Students' own tests" hint="Is a submission with no tests missing something?"
              source="the base rubric"
              inherited={(rubric?.testing_required ?? false) ? "yes" : "no"}
              bound={b.testing_required == null ? null : (b.testing_required ? "yes" : "no")}
              pending={tests} onChange={setTests}
              options={[{ id: "yes", label: "expected" }, { id: "no", label: "not expected" }]}
              disabled={ro} />
          </div>
        </section>
      </div>

      <div role="tabpanel" id={`${ids}-setup-panel`} aria-labelledby={`${ids}-setup`}
           hidden={tab !== "setup"}>
        {/* 🔴 DEFAULTS AND OVERRIDES, NOT A PARTITION. A cohort setting is a default an assignment
            may override, and the inherited value is shown in place, so "where is this set?" is
            answered where it is asked. The pair of dropdown lines this replaced — "— leave as
            claude-code —" and "— clear, use the system default —" — was two near-identical
            sentences, one writing nothing and one archiving every grade. */}
        <div className="set-rows">
          {/* One base for every assignment since 2026-09-27, so usually nothing to pick: the
              picker renders only while more than one rubric is selectable. */}
          {selectable.length > 1 && (
            <Inherited
              label="Rubric" hint="What every criterion comes from."
              inherited={defaultRubricId} bound={b.rubric_id ?? null}
              pending={rubricSel} onChange={setRubricSel} disabled={ro}
              options={selectable.map((x) => ({ id: x.id, label: x.id, note: x.brief }))} />)}
          <Inherited
            label="Engine" hint="What marks this assignment."
            inherited={sysEngine} bound={b.engine_id ?? null}
            pending={engine} onChange={setEngine} disabled={ro}
            options={harnesses.map((h) => ({ id: h.id, label: h.label, note: h.note }))} />
          <Inherited
            label="Unattended grading" hint="Grades new hand-ins without anybody pressing Run."
            /* ⚠️ THE COHORT SWITCH'S VALUE, NOT THE WORD "inherit". */
            inherited={sysAuto ? "on" : "off"}
            bound={b.auto_grade == null ? null : (b.auto_grade ? "on" : "off")}
            pending={auto} onChange={setAuto}
            options={[{ id: "on", label: "on" }, { id: "off", label: "off" }]}
            extra={<>
              <Why label="Why unattended grading needs two switches">
                The cohort's switch on the Grading page and this one must both say on; each alone
                does nothing. A Run somebody presses needs neither — it is that decision.
              </Why>
              {effAuto && !sysAuto && (
                <span className="small warn">The cohort's switch is off, so nothing runs
                  unattended yet.</span>)}
            </>} disabled={ro} />
        </div>
      </div>

      {err && <ErrorBox err={err} />}
      {changes.length > 0 && canEdit && (
        <div className="savebar" role="status">
          {review === null ? (<>
            <span className="as-save-what">
              <b>{changes.length} unsaved change{changes.length === 1 ? "" : "s"}</b>
              <span className="muted"> · {changes.map((c) => c.what).join(" · ")}</span>
            </span>
            <span className="spacer" />
            <button className="btn ghost" type="button" disabled={busy} onClick={discard}>Discard</button>
            <button className="btn primary" type="button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : regrades ? "Review and save" : "Save"}</button>
          </>) : (<>
            {/* The server's own sentence: how many marks this archives and re-queues. */}
            <span className="as-save-what">{review}</span>
            <span className="spacer" />
            <button className="btn ghost" type="button" disabled={busy}
                    onClick={() => setReview(null)}>Back</button>
            <button className="btn primary" type="button" disabled={busy} onClick={confirm}>
              {busy ? "Saving…" : "Save and re-grade"}</button>
          </>)}
        </div>)}
      {said && changes.length === 0 && <p className="small as-said" role="status">{said}</p>}
    </div>
  );
}

// ── Evaluation: the criteria ────────────────────────────────────────────────────

/**
 * Every criterion this assignment is marked on: the rubric's own, then the assignment's.
 *
 * 🔴 THE POINTS ARE THE SERVER'S ARITHMETIC, MIRRORED. `effectivePoints` is gated against
 * `rubric.effective` across every mix of Counts / Advisory / Off, the refusals included, so the
 * number beside a criterion is the one grading will use and the warning under the list names
 * exactly what the server would refuse.
 * 🔴 WHY THE SWITCH EXISTS. `generic-v1`'s C2 credited "invalid input is rejected"; five one-shot
 * devnet scripts were marked against it, and the grader wrote that the bullet did not apply and
 * scored it 0 of 30 anyway. Advisory, Off and the assignment's own lines are the remedy.
 * ⚠️ EXTRAS ARE KEYED BY POSITION, as the server keys them (`X1…Xn`), so removing one moves the
 * settings of the ones after it down with them rather than onto a different criterion.
 */
function Criteria({ base, extras, overrides, onExtras, onOverrides, readOnly, tests, tasks }: {
  base: Criterion[]; extras: ExtraCriterion[]; overrides: Record<string, CriterionOverride>;
  onExtras: (v: ExtraCriterion[]) => void;
  onOverrides: (v: Record<string, CriterionOverride>) => void;
  readOnly: boolean;
  tests: TestsDraft;
  /** The assignment's tasks, `T1 · name`: they split C1, and a test under C1 may prove one. */
  tasks: string[];
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const max = base.reduce((n, c) => n + (c.weight || 0), 0);
  const xs = extras.map((c, i) => ({ ...c, key: `X${i + 1}` }));
  const modeOf = (id: string) => ovOf(overrides, id).mode;
  const pts = effectivePoints(base, xs.map((x) => ({ id: x.key, weight: Number(x.weight) || 0 })),
                              modeOf, max);
  const byId = new Map(pts.rows.map((r) => [r.id, r]));
  const total = base.length + xs.length;
  const choices: CriterionChoice[] = [
    ...base.map((c) => ({ id: c.id, label: c.label ?? c.id,
                          ...(c.id === "C1" && tasks.length ? { tasks } : {}) })),
    ...xs.map((c) => ({ id: c.key, label: c.label?.trim() || "your criterion" }))];
  const testsOn = (id: string) => tests.draft.filter((x) => criterionOf(x) === id).length;

  const setOv = (id: string, patch: Partial<CriterionOverride>) => {
    const next = { ...ovOf(overrides, id), ...patch };
    const out = { ...overrides };
    if (bare(tidy(next)) && next.credit_for.length === 0 && next.note === "") delete out[id];
    else out[id] = next;
    onOverrides(out);
  };
  const toggle = (id: string) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const setExtra = (i: number, patch: Partial<ExtraCriterion>) =>
    onExtras(extras.map((c, j) => (i === j ? { ...c, ...patch } : c)));
  const removeExtra = (i: number) => {
    const out: Record<string, CriterionOverride> = {};
    for (const [k, v] of Object.entries(overrides)) {
      const m = /^X(\d+)$/.exec(k);
      const n = m ? Number(m[1]) : 0;
      if (!m) out[k] = v;
      else if (n !== i + 1) out[n > i + 1 ? `X${n - 1}` : k] = v;
    }
    onOverrides(out);
    onExtras(extras.filter((_, j) => j !== i));
    // ⚠️ ITS TESTS GO WITH IT, and the later criteria's move down with theirs — named in the save
    // bar and undone by Discard, never left pointing at a criterion that is now another one.
    tests.recriterion((c) => {
      const m = /^X(\d+)$/.exec(c);
      const n = m ? Number(m[1]) : 0;
      return !m ? c : n === i + 1 ? null : n > i + 1 ? `X${n - 1}` : c;
    });
    setOpen(new Set());
  };
  const addExtra = () => {
    onExtras([...extras, { label: "", weight: 10 }]);
    setOpen((s) => new Set(s).add(`X${extras.length + 1}`));
  };

  const row = (id: string, name: ReactNode, lines: string[], extra: number | null) => {
    const o = ovOf(overrides, id);
    const mode = o.mode;
    const p = byId.get(id);
    const mine = o.credit_for.filter((x) => x.trim()).length;
    const isOpen = open.has(id);
    const nTests = testsOn(id);
    const sub = mode === "advisory" ? "judged and reported, counts nothing"
      : mode === "off" ? "not judged"
      : [extra !== null ? "your criterion" : `${lines.length} line${lines.length === 1 ? "" : "s"} from the base`,
         mine ? `${mine} of yours` : "", o.replaces && mine ? "only yours" : ""]
        .filter(Boolean).join(" · ");
    const parts = [sub, nTests ? `${nTests} test${nTests === 1 ? "" : "s"}` : "",
                   id === "C1" && tasks.length && mode !== "off"
                     ? `split into ${tasks.length} task${tasks.length === 1 ? "" : "s"} on Brief` : ""];
    const subline = parts.filter(Boolean).join(" · ");
    return (
      <li key={id} className={`as-crit${mode === "off" ? " off" : ""}`}>
        <div className="as-crit-row">
          <button className="btn sm ghost as-crit-toggle" type="button" aria-expanded={isOpen}
                  aria-label={`${isOpen ? "Hide" : "Show"} what ${id} credits`}
                  onClick={() => toggle(id)}>{isOpen ? "▾" : "▸"}</button>
          <code>{id}</code>
          <div className="as-crit-name">{name}<small>{subline}</small></div>
          {extra !== null
            ? <input className="input as-crit-weight" type="number" min={1} step={1}
                     disabled={readOnly} aria-label={`Points for ${id}`} value={String(extras[extra]?.weight ?? "")}
                     onChange={(e) => setExtra(extra, { weight: Number(e.target.value) })} />
            : <span className={`as-crit-pts${mode === "advisory" ? " would" : ""}`}>
                {mode === "off" ? "—"
                  : mode === "advisory" ? <><span className="sr">would be </span>{p?.would ?? 0}</>
                  : (p?.points ?? "—")}</span>}
          <div className="seg" role="group" aria-label={`What ${id} does here`}>
            {MODES.map((m) => (
              <button key={m.id} type="button" aria-pressed={mode === m.id} disabled={readOnly}
                      onClick={() => setOv(id, { mode: m.id })}>{m.label}</button>))}
          </div>
          {extra !== null
            ? <button className="btn sm ghost" type="button" aria-label={`Remove ${id}`}
                      disabled={readOnly} onClick={() => removeExtra(extra)}>✕</button>
            : <span />}
        </div>
        {isOpen && (
          <div className="as-crit-body">
            {lines.length > 0 && (
              <ul className="as-crit-lines">{lines.map((l, i) => (
                <li key={i} className={o.replaces && mine ? "gone" : undefined}>{l}</li>))}</ul>)}
            {mode === "off"
              ? <p className="small muted">Off: the grader is never shown {id}, so nothing written
                  for it here is sent.</p>
              : <CriterionWords id={id} o={o} hasBase={lines.length > 0} disabled={readOnly}
                                onChange={(patch) => setOv(id, patch)} />}
            <TestList t={tests} criterion={id} criteria={choices} off={mode === "off"} />
          </div>)}
      </li>);
  };

  const counting = pts.rows.filter((r) => r.points !== null);
  return (
    <section className="as-sec first" aria-labelledby="crit-h">
      <div className="as-sec-head">
        <h4 className="as-sec-t" id="crit-h">Criteria</h4>
        <Why label="How the points are shared">
          The criteria that count share the assignment's {max} points. Yours keep the points you
          give them; the rubric's own share what is left, in proportion. Advisory is judged and
          reported and counts nothing; Off is not judged. Changing what a criterion does re-grades
          this assignment's marks.
        </Why>
        <span className="as-sec-aside">{pts.counting} of {total} counting · {max} points</span>
      </div>
      <ul className="as-crits">
        {base.map((c) => row(c.id, c.label ?? c.id, c.credit_for, null))}
        {xs.map((c, i) => row(c.key,
          <input className="input as-crit-label" type="text" value={c.label ?? ""} disabled={readOnly}
                 aria-label={`What ${c.key} is worth marks for`}
                 placeholder="What this criterion is worth marks for"
                 onChange={(e) => setExtra(i, { label: e.target.value })} />,
          c.credit_for ?? [], i))}
      </ul>
      <div className="row gap">
        <button className="btn sm" type="button" disabled={readOnly} onClick={addExtra}>
          + Add a criterion</button>
      </div>
      <TestList t={tests} criteria={choices} />
      {pts.refused
        ? <p className="alert small refused" role="alert">
            {REFUSAL[pts.refused]({ total: pts.extraTotal, max, zeroed: pts.zeroed })}</p>
        : (
          <div className="pts-split">
            <div className="pts-bar" aria-hidden="true">
              {counting.map((r) => (
                <i key={r.id} className={r.id.startsWith("X") ? "x" : undefined}
                   style={{ width: `${((r.points ?? 0) / Math.max(1, max)) * 100}%` }} />))}
            </div>
            <div className="pts-key">
              {pts.rows.map((r) => (
                <span key={r.id}>{r.mode === "score" ? <><b>{r.id}</b> {r.points}</>
                  : r.mode === "advisory" ? <>{r.id} advisory · would be {r.would}</>
                  : <>{r.id} off</>}</span>))}
            </div>
          </div>)}
      <TestsFoot t={tests} />
    </section>
  );
}

/**
 * What one criterion credits on this assignment, in the teacher's words.
 * ⚠️ THE TEXT BOX KEEPS WHAT IS TYPED, blank lines included, and hands on only the lines. The old
 * one rebuilt its value from the filtered list on every keystroke, so pressing Enter at the end of
 * a line was undone at once and a second line could only be pasted in.
 * ⚠️ `credit_for` APPENDS UNLESS "Use only my lines" IS TICKED — silently redefining a criterion
 * that keeps its id, label and weight would make two assignments' marks incomparable.
 */
function CriterionWords({ id, o, hasBase, disabled, onChange }: {
  id: string; o: CriterionOverride; hasBase: boolean; disabled: boolean;
  onChange: (patch: Partial<CriterionOverride>) => void;
}) {
  const [text, setText] = useState(o.credit_for.join("\n"));
  const ids = useId();
  const mine = o.credit_for.some((x) => x.trim());
  return (<>
    <label className="as-field" htmlFor={`${ids}-lines`}>Your lines for this assignment
      <textarea id={`${ids}-lines`} className="ta prose short" rows={2} value={text} disabled={disabled}
                placeholder={`One per line: what ${id} credits on this assignment.`}
                onChange={(e) => {
                  setText(e.target.value);
                  const lines = e.target.value.split("\n").map((x) => x.trim()).filter(Boolean);
                  onChange({ credit_for: lines, ...(lines.length ? {} : { replaces: false }) });
                }} />
    </label>
    {hasBase && (
      <label className="small as-check">
        <input type="checkbox" checked={o.replaces && mine} disabled={disabled || !mine}
               onChange={(e) => onChange({ replaces: e.target.checked })} />
        {" "}Use only my lines</label>)}
    <label className="as-field" htmlFor={`${ids}-note`}>Note to the grader
      <textarea id={`${ids}-note`} className="ta prose short" rows={2} value={o.note} disabled={disabled}
                placeholder={`Optional: what this assignment means by ${id}, or why it is advisory.`}
                onChange={(e) => onChange({ note: e.target.value })} />
    </label>
  </>);
}

// ── a setting with a default ────────────────────────────────────────────────────

/**
 * One setting with a default and an optional override for this assignment.
 *
 * 🔴 THREE STATES, EACH ITS OWN CONTROL: inheriting (the default's value and Override), overriding
 * (a select and "Use the cohort default"), and the default itself named beside the override —
 * so a teacher cannot pick one while reading another. Clearing an override archives grades; the
 * save bar's review says so before it happens.
 * ⚠️ CLEARING AN OVERRIDE THAT WAS NEVER SAVED IS NO CHANGE AT ALL: it goes back to "leave
 * alone", not to "inherit", which would stamp an actor on a field nobody changed.
 */
function Inherited({ label, hint, inherited, bound, pending, onChange, options, source, extra,
                     disabled = false }: {
  label: string; hint?: string; inherited: string; bound: string | null; pending: string;
  onChange: (v: string) => void;
  options: { id: string; label: string; note?: string; disabled?: boolean }[];
  /** Where the default comes from, when it is not the cohort's settings. */
  source?: string;
  extra?: ReactNode;
  disabled?: boolean;
}) {
  const overriding = pending !== "" ? pending !== "inherit" : bound != null;
  const effective = pending === "inherit" ? inherited : pending !== "" ? pending : (bound ?? inherited);
  const name = (id: string) => options.find((o) => o.id === id)?.label ?? id;
  return (
    <div className="set-row">
      <div className="set-row-k">{label}{hint && <small>{hint}</small>}</div>
      <div className="set-row-v">
        {overriding
          ? <select className="select" aria-label={label} value={effective} disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}>
              {options.map((o) =>
                <option key={o.id} value={o.id} title={o.note} disabled={o.disabled}>{o.label}</option>)}
            </select>
          : <b>{name(effective)}</b>}
        {overriding
          ? <Badge tone="warn" title="This assignment overrides the default.">overridden</Badge>
          : <Badge tone="plain">{source ? `from ${source}` : "from the cohort"}</Badge>}
        {overriding && (
          <span className="small muted">{source ? "default" : "cohort default"}: {name(inherited)}</span>)}
        {extra}
      </div>
      {overriding
        ? <button className="btn sm ghost" type="button" disabled={disabled}
                  onClick={() => onChange(bound == null ? "" : "inherit")}>
            {source ? "Use the default" : "Use the cohort default"}</button>
        : <button className="btn sm" type="button" disabled={disabled}
                  onClick={() => onChange(effective || options.find((o) => !o.disabled)?.id || "")}>
            Override</button>}
      {/* 🔴 A TOOLTIP ON A DISABLED OPTION IS UNREACHABLE — you cannot hover what you cannot open. */}
      {options.filter((o) => o.disabled).map((o) => (
        <div key={o.id} className="set-row-note small warn">{o.label.replace(" — unavailable", "")}: {o.note}</div>))}
    </div>
  );
}
